import { useQueryClient } from '@tanstack/react-query';
import { CreateAutodeskLayerApiRequest } from '../../../../types/requests/autodesk-layers/create-autodesk-layer.request';
import { UpdateAutodeskLayerApiRequest } from '../../../../types/requests/autodesk-layers/update-autodesk-layer.request';
import { DeleteAutodeskLayerApiRequest } from '../../../../types/requests/autodesk-layers/delete-autodesk-layer.request';
import { GetAutodeskLayerApiRequest } from '../../../../types/requests/autodesk-layers/get-autodesk-layer.request';
import { SearchAutodeskLayersApiRequest } from '../../../../types/requests/autodesk-layers/search-autodesk-layers.request';
import { AutodeskLayerApiModel } from '../../../../types/models/autodesk-layer.model';
import {
  createAutodeskLayer,
  deleteAutodeskLayer,
  getAutodeskLayer,
  searchAutodeskLayers,
  updateAutodeskLayer,
} from '../../api/autodesk-layers.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'autodesk-layer';
const searchQueryKey = 'search';

export const useCreateAutodeskLayer = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateAutodeskLayerApiRequest) => (await createAutodeskLayer(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, autodeskLayer: data }] as const;
};

export const useUpdateAutodeskLayer = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateAutodeskLayerApiRequest) => (await updateAutodeskLayer(params)).data,
    onSuccess: (autodeskLayer) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as GetAutodeskLayerApiRequest)?.autodeskLayerId === autodeskLayer.id),
      }),
  });
  return [request, { ...result, autodeskLayer: data }] as const;
};

export const useGetAutodeskLayer = <T extends AutodeskLayerApiModel>(params: GetAutodeskLayerApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAutodeskLayer<T>(params)).data,
    enabled: Boolean(params.autodeskLayerId && !Number.isNaN(params.autodeskLayerId)),
  });
  return { ...result, autodeskLayer: data } as const;
};

export const useSearchAutodeskLayers = (params: SearchAutodeskLayersApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchAutodeskLayers({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteAutodeskLayer = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteAutodeskLayerApiRequest) => (await deleteAutodeskLayer(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
